'use client'
import * as React from 'react'
import { LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import { SafeImage } from '@/components/marketing/safe-image'
import { cn } from '@/lib/utils'
import { EASE, fadeUpItem, staggerContainer, VIEWPORT } from '@/lib/motion'
import { Heart, MessageCircle, Play, Send, ShoppingBag, Star, Tag } from 'lucide-react'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'
import { demoProducts, type DemoPost, type DemoProduct, getDemoProduct } from '@/lib/copy/demo-products'
import { AddToCartModal } from './add-to-cart-modal'

type Img = { ref: string }

// Always use lifestyle Unsplash photos for the showcase, the
// crawled marketing-site screenshots don't fit a "live playground"
// feel. We still accept `images` for callers that want to seed the
// pool, but the primary visuals come from a curated lifestyle pool.
function buildPool(seed: string, extras: string[]): string[] {
  const lifestyleCats = ['ugc', 'fashion', 'beauty', 'home', 'commerce', 'jewellery', 'furniture', 'hospitality', 'sport', 'electronics', 'editorial'] as const
  const lifestyle = Array.from({ length: 14 }, (_, i) =>
    unsplashPlaceholderFor(`${seed}-life-${i}`, { category: lifestyleCats[i % lifestyleCats.length], width: 800, height: 800 })
  )
  return [...lifestyle, ...extras]
}

type Layout = {
  key: string
  label: string
  caption: string
  render: (props: { imgs: string[]; openProduct: (id: string) => void }) => React.ReactNode
}

const productIds = Object.keys(demoProducts)
const productAt = (i: number): DemoProduct => demoProducts[productIds[i % productIds.length]]

// Shared hover/focus lift for the layout tiles. Matches the repo's card
// idiom (`hover:-translate-y-0.5`); motion-safe so reduced-motion users
// get a static tile. Tailwind's `transition` shorthand already covers
// transform, so tiles that carry it just compose.
const LIFT =
  'motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:focus-visible:-translate-y-0.5'

export function WidgetLayoutShowcase({ images = [], fillerSlug = 'showcase' }: { images?: Img[]; fillerSlug?: string }) {
  const refs = buildPool(fillerSlug, images.map((i) => i.ref).filter(Boolean))
  const [active, setActive] = React.useState<string>('grid')
  const [activeProduct, setActiveProduct] = React.useState<string | null>(null)
  const reduce = useReducedMotion()

  const layouts: Layout[] = [
    {
      key: 'grid',
      label: 'Grid',
      caption: 'The default. 3 / 4 / 6 columns, AI-tagged products on every tile.',
      render: ({ imgs, openProduct }) => (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {imgs.slice(0, 9).map((src, i) => {
            const p = productAt(i)
            return (
              <div key={i} className={cn(LIFT, 'group relative aspect-square overflow-hidden rounded-md bg-muted')}>
                <SafeImage src={src} alt="" fill sizes="(max-width: 768px) 33vw, 200px" className="object-cover transition group-hover:scale-105" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-2 text-[10px] text-white">
                  <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {120 + i * 47}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {6 + i * 3}</span>
                </div>
                <button
                  type="button"
                  aria-label={`Shop ${p.name}`}
                  onClick={() => openProduct(p.id)}
                  className="absolute z-10 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-lg ring-2 ring-white/60 transition hover:scale-110 focus:outline-none focus:ring-4 focus:ring-primary/40"
                  style={{ left: `${42 + ((i * 7) % 30)}%`, top: `${44 + ((i * 11) % 32)}%` }}
                >
                  <ShoppingBag className="h-3 w-3 text-primary" />
                  <span className="absolute inset-0 -z-10 animate-ping motion-reduce:animate-none rounded-full bg-white/80" />
                </button>
              </div>
            )
          })}
        </div>
      ),
    },
    {
      key: 'carousel',
      label: 'Carousel',
      caption: 'A horizontal row that auto-advances. Best for above-the-fold storytelling.',
      render: ({ imgs, openProduct }) => (
        <div className="overflow-hidden">
          <div className="flex gap-3 pb-2">
            {imgs.slice(0, 8).map((src, i) => {
              const p = productAt(i + 1)
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => openProduct(p.id)}
                  className={cn(LIFT, 'group relative aspect-[3/4] w-44 shrink-0 overflow-hidden rounded-lg bg-muted text-left sm:w-56')}
                >
                  <SafeImage src={src} alt="" fill sizes="220px" className="object-cover transition group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium text-foreground">
                      <Tag className="h-3 w-3 text-primary" /> Tagged · {p.price}
                    </span>
                    <p className="mt-1.5 text-[11px] text-white">@happy_customer_{i + 1}</p>
                  </div>
                </button>
              )
            })}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">← drag to scroll · auto-advances every 4s</p>
        </div>
      ),
    },
    {
      key: 'pdp',
      label: 'PDP embed',
      caption: 'A UGC strip pinned under the product gallery. Lifted PDP CR +22% across DTC storefronts.',
      render: ({ imgs, openProduct }) => {
        const p = productAt(0)
        return (
          <div className="overflow-hidden rounded-xl border bg-background">
            <div className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr]">
              <div className="relative aspect-square bg-muted">
                <SafeImage src={imgs[0]} alt="" fill sizes="(max-width: 768px) 100vw, 50vw" className="object-cover" />
                <button
                  type="button"
                  aria-label={`Shop ${p.name}`}
                  onClick={() => openProduct(p.id)}
                  className="absolute z-10 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-xl ring-2 ring-white/70 transition hover:scale-110"
                  style={{ left: '54%', top: '52%' }}
                >
                  <ShoppingBag className="h-4 w-4 text-primary" />
                  <span className="absolute inset-0 -z-10 animate-ping motion-reduce:animate-none rounded-full bg-white/80" />
                </button>
              </div>
              <div className="flex flex-col gap-3 p-5 sm:p-6">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Product detail</p>
                <h4 className="font-display text-xl font-medium leading-tight">{p.name}</h4>
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-medium">{p.price}</span>
                  <span className="text-xs text-muted-foreground line-through">£118</span>
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">25% off</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />)}
                  <span className="ml-1 text-muted-foreground">{p.rating} · {p.reviews} reviews</span>
                </div>
                <p className="text-xs text-muted-foreground">{p.blurb}</p>
                <div className="mt-1 flex gap-2">
                  <button type="button" onClick={() => openProduct(p.id)} className="flex-1 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition hover:opacity-90">
                    Add to bag
                  </button>
                  <button type="button" className="rounded-md border px-3 py-2 text-xs font-medium">♡</button>
                </div>
              </div>
            </div>
            <div className="border-t bg-secondary/30 p-3 sm:p-4">
              <p className="mb-2 text-[10px] font-medium uppercase tracking-widest text-primary">Real customers wearing this · Idukki UGC</p>
              <div className="flex gap-2 overflow-hidden">
                {imgs.slice(1, 7).map((src, i) => {
                  const sp = productAt(i + 2)
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => openProduct(sp.id)}
                      className={cn(LIFT, 'group relative aspect-square w-16 shrink-0 overflow-hidden rounded-md bg-muted sm:w-20')}
                    >
                      <SafeImage src={src} alt="" fill sizes="80px" className="object-cover transition group-hover:scale-105" />
                      <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white/95 text-foreground">
                        <ShoppingBag className="h-2.5 w-2.5 text-primary" />
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )
      },
    },
    {
      key: 'stories',
      label: 'Stories',
      caption: 'Tap-through fullscreen stories, the format your customers know best.',
      render: ({ imgs, openProduct }) => (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {imgs.slice(0, 8).map((src, i) => {
            const p = productAt(i)
            return (
              <button
                type="button"
                key={i}
                onClick={() => openProduct(p.id)}
                className="flex w-20 shrink-0 flex-col items-center gap-1.5 transition hover:opacity-90 motion-safe:hover:-translate-y-0.5 motion-safe:focus-visible:-translate-y-0.5"
              >
                <div className="rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-fuchsia-500 p-[2px]">
                  <div className="rounded-full bg-background p-[2px]">
                    <div className="relative h-16 w-16 overflow-hidden rounded-full bg-muted">
                      <SafeImage src={src} alt="" fill sizes="64px" className="object-cover" />
                    </div>
                  </div>
                </div>
                <span className="truncate text-[10px] text-muted-foreground">@user_{i + 1}</span>
              </button>
            )
          })}
        </div>
      ),
    },
    {
      key: 'wall',
      label: 'Hashtag wall',
      caption: 'Live #campaign feed. Aggregates posts across every channel into one masonry.',
      render: ({ imgs, openProduct }) => (
        <div className="grid grid-cols-4 gap-2 sm:gap-3">
          {imgs.slice(0, 12).map((src, i) => {
            const p = productAt(i)
            return (
              <button
                type="button"
                key={i}
                onClick={() => openProduct(p.id)}
                className={cn(
                  LIFT,
                  'group relative overflow-hidden rounded-md bg-muted',
                  i % 7 === 0 && 'col-span-2 row-span-2 aspect-square',
                  i % 5 === 1 && 'aspect-[3/4]',
                  ![0, 1].includes(i % 7) && 'aspect-square',
                )}
              >
                <SafeImage src={src} alt="" fill sizes="(max-width: 768px) 25vw, 150px" className="object-cover transition group-hover:scale-105" />
                <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/95 text-foreground opacity-0 transition group-hover:opacity-100">
                  <ShoppingBag className="h-2.5 w-2.5 text-primary" />
                </span>
              </button>
            )
          })}
        </div>
      ),
    },
    {
      key: 'lookbook',
      label: 'Lookbook',
      caption: 'Magazine-style flipbook with hotspot products. Replaces the seasonal PDF catalogue.',
      render: ({ imgs, openProduct }) => {
        const p = productAt(0)
        return (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg sm:col-span-2">
              <SafeImage src={imgs[0]} alt="" fill sizes="(max-width: 768px) 100vw, 60vw" className="object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-6">
                <p className="text-[10px] font-medium uppercase tracking-widest opacity-80">Spring · 2026</p>
                <h4 className="mt-1 font-display text-xl font-medium md:text-2xl">The relaxed silhouette</h4>
                <p className="mt-1 text-xs opacity-90">12 looks · 38 products tagged</p>
              </div>
              <button
                type="button"
                onClick={() => openProduct(p.id)}
                aria-label={`Shop ${p.name}`}
                className="absolute z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-xl ring-2 ring-white/70 transition hover:scale-110"
                style={{ left: '46%', top: '54%' }}
              >
                <ShoppingBag className="h-4 w-4 text-primary" />
                <span className="absolute inset-0 -z-10 animate-ping motion-reduce:animate-none rounded-full bg-white/80" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-1 sm:gap-3">
              {imgs.slice(1, 4).map((src, i) => {
                const sp = productAt(i + 1)
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => openProduct(sp.id)}
                    className={cn(LIFT, 'group relative aspect-square overflow-hidden rounded-md bg-muted')}
                  >
                    <SafeImage src={src} alt="" fill sizes="(max-width: 768px) 50vw, 200px" className="object-cover transition group-hover:scale-105" />
                    <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/95 text-foreground opacity-0 transition group-hover:opacity-100">
                      <ShoppingBag className="h-2.5 w-2.5 text-primary" />
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )
      },
    },
    {
      key: 'masonry',
      label: 'Masonry',
      caption: 'Pinterest-style packed grid. Best for high-density social walls, heights stagger automatically.',
      render: ({ imgs, openProduct }) => (
        <div className="columns-2 gap-2 sm:columns-3 sm:gap-3 [column-fill:_balance]">
          {imgs.slice(0, 10).map((src, i) => {
            const p = productAt(i)
            const h = [180, 240, 300, 200, 280, 260, 220, 320, 240, 200][i % 10]
            return (
              <button
                type="button"
                key={i}
                onClick={() => openProduct(p.id)}
                className={cn(LIFT, 'group relative mb-2 block w-full overflow-hidden rounded-md bg-muted sm:mb-3')}
                style={{ height: h }}
              >
                <SafeImage src={src} alt="" fill sizes="(max-width: 768px) 50vw, 200px" className="object-cover transition group-hover:scale-105" />
                <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/95 text-foreground opacity-0 transition group-hover:opacity-100">
                  <ShoppingBag className="h-2.5 w-2.5 text-primary" />
                </span>
              </button>
            )
          })}
        </div>
      ),
    },
    {
      key: 'bento',
      label: 'Bento',
      caption: 'One hero tile + a grid of supporting posts. Lifts the campaign hero post above the noise.',
      render: ({ imgs, openProduct }) => {
        const p = productAt(0)
        return (
          <div className="grid grid-cols-3 grid-rows-2 gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => openProduct(p.id)}
              className={cn(LIFT, 'group relative col-span-2 row-span-2 overflow-hidden rounded-lg bg-muted')}
            >
              <SafeImage src={imgs[0]} alt="" fill sizes="(max-width: 768px) 66vw, 400px" className="object-cover transition group-hover:scale-105" />
              <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium text-foreground shadow">
                <Tag className="h-3 w-3 text-primary" /> Hero · tagged
              </span>
              <span
                className="absolute z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-xl ring-2 ring-white/70"
                style={{ left: '52%', top: '58%' }}
              >
                <ShoppingBag className="h-3.5 w-3.5 text-primary" />
                <span className="absolute inset-0 -z-10 animate-ping motion-reduce:animate-none rounded-full bg-white/80" />
              </span>
            </button>
            {imgs.slice(1, 5).map((src, i) => {
              const sp = productAt(i + 1)
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => openProduct(sp.id)}
                  className={cn(LIFT, 'group relative aspect-square overflow-hidden rounded-md bg-muted')}
                >
                  <SafeImage src={src} alt="" fill sizes="200px" className="object-cover transition group-hover:scale-105" />
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white/95">
                    <ShoppingBag className="h-2 w-2 text-primary" />
                  </span>
                </button>
              )
            })}
          </div>
        )
      },
    },
    {
      key: 'reel',
      label: 'Reel scroll',
      caption: 'Vertical full-screen reel feed, TikTok-Shop style. Inline product cards drop in as the video plays.',
      render: ({ imgs, openProduct }) => {
        const p = productAt(0)
        return (
          <div className="flex items-center justify-center gap-3">
            {[0, 1, 2].map((idx) => (
              <div
                key={idx}
                className={`relative overflow-hidden rounded-2xl border-[5px] border-slate-900 bg-slate-900 shadow-xl ${idx === 1 ? 'h-80 w-44 ring-2 ring-primary/40' : 'h-60 w-32 opacity-80'}`}
              >
                <SafeImage src={imgs[idx]} alt="" fill sizes="160px" className="object-cover" />
                {idx === 1 && (
                  <>
                    <div className="absolute inset-x-2 top-2 flex gap-0.5">
                      <span className="h-0.5 flex-1 rounded-full bg-white" />
                      <span className="h-0.5 flex-1 rounded-full bg-white/40" />
                      <span className="h-0.5 flex-1 rounded-full bg-white/40" />
                    </div>
                    <Play className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 fill-white text-white drop-shadow-lg" />
                    <button
                      type="button"
                      onClick={() => openProduct(p.id)}
                      className="absolute inset-x-2 bottom-2 flex items-center gap-2 rounded-lg bg-white/95 p-1.5 text-left shadow-xl"
                    >
                      <div className="h-7 w-7 shrink-0 rounded bg-gradient-to-br from-amber-200 to-rose-300" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[9px] font-semibold text-foreground">{p.name}</p>
                        <p className="text-[9px] text-primary">{p.price} · Tap to add</p>
                      </div>
                      <ShoppingBag className="h-3 w-3 text-primary" />
                    </button>
                    <span className="absolute right-2 top-6 flex flex-col items-center gap-2 text-white">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 backdrop-blur"><Heart className="h-3.5 w-3.5" /></span>
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 backdrop-blur"><MessageCircle className="h-3.5 w-3.5" /></span>
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 backdrop-blur"><Send className="h-3.5 w-3.5" /></span>
                    </span>
                  </>
                )}
                {idx !== 1 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="h-4 w-4 fill-white text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      },
    },
    {
      key: 'wallOfLove',
      label: 'Wall of love',
      caption: 'Testimonial mosaic, review quotes alternating with creator portraits. Trust block under the fold.',
      render: ({ imgs, openProduct }) => {
        const quotes = [
          { q: 'Fits true to size, soft as advertised.', who: '@maya.k' },
          { q: '5 stars, better than my old brand by miles.', who: '@iris.skin' },
          { q: 'Ordered another in cream the same week.', who: '@sky.b' },
          { q: 'Worth every penny.', who: '@otto.frame' },
        ]
        return (
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {imgs.slice(0, 3).map((src, i) => {
              const p = productAt(i)
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => openProduct(p.id)}
                  className={cn(LIFT, 'group relative aspect-[3/4] overflow-hidden rounded-md bg-muted')}
                >
                  <SafeImage src={src} alt="" fill sizes="(max-width: 768px) 33vw, 200px" className="object-cover transition group-hover:scale-105" />
                  <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/95">
                    <ShoppingBag className="h-2.5 w-2.5 text-primary" />
                  </span>
                </button>
              )
            })}
            {quotes.map((qt, i) => (
              <div key={i} className="flex aspect-[3/4] flex-col justify-between rounded-md border bg-card p-3">
                <div className="flex gap-0.5 text-[10px] text-amber-500">
                  {[0,1,2,3,4].map((s) => <Star key={s} className="h-3 w-3 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-[11px] font-medium leading-snug text-foreground">&ldquo;{qt.q}&rdquo;</p>
                <p className="text-[10px] text-muted-foreground">{qt.who}</p>
              </div>
            ))}
            <button
              type="button"
              onClick={() => openProduct(productAt(4).id)}
              className={cn(LIFT, 'group relative col-span-2 aspect-[6/4] overflow-hidden rounded-md bg-muted')}
            >
              <SafeImage src={imgs[3]} alt="" fill sizes="(max-width: 768px) 66vw, 400px" className="object-cover transition group-hover:scale-105" />
              <span className="absolute left-2 bottom-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium text-foreground shadow">
                <ShoppingBag className="h-3 w-3 text-primary" /> Shop the look
              </span>
            </button>
          </div>
        )
      },
    },
  ]

  const current = layouts.find((l) => l.key === active) || layouts[0]
  const product = activeProduct ? getDemoProduct(activeProduct) : null

  return (
    <LazyMotion features={domAnimation}>
      {/* Stagger-reveal once on scroll-in: header, tab rail, then preview. */}
      <m.div
        className="overflow-hidden rounded-2xl border bg-card shadow-sm"
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={VIEWPORT}
        variants={staggerContainer}
      >
        <m.div className="border-b p-4 sm:p-5" variants={reduce ? undefined : fadeUpItem}>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-primary">Layout showcase · click any pin</p>
          <h3 className="mt-1 font-display text-base font-medium tracking-tight sm:text-lg">
            Six ways the same content can ship.
          </h3>
          <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{current.caption}</p>
        </m.div>
        <m.div className="overflow-x-auto border-b" variants={reduce ? undefined : fadeUpItem}>
          <ul role="tablist" className="flex min-w-max">
            {layouts.map((l) => (
              <li key={l.key} role="presentation" className="shrink-0">
                <button
                  type="button"
                  role="tab"
                  aria-selected={active === l.key}
                  onClick={() => setActive(l.key)}
                  className={cn(
                    'border-b-2 px-4 py-3 text-xs font-medium transition sm:text-sm',
                    active === l.key
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                  )}
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </m.div>
        <m.div className="bg-secondary/30 p-4 sm:p-5" variants={reduce ? undefined : fadeUpItem}>
          {/* Re-key on the active layout so switching tabs settles in with a
              quiet opacity/scale transition rather than a hard swap. */}
          <m.div
            key={current.key}
            initial={reduce ? false : { opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            {current.render({ imgs: refs, openProduct: setActiveProduct })}
          </m.div>
        </m.div>
      </m.div>

      <AddToCartModal product={product} onClose={() => setActiveProduct(null)} />
    </LazyMotion>
  )
}
